import React, { useEffect, useMemo, useState } from 'react';
import { Card, Collapse } from 'reactstrap';
import {
  getCatalogItem,
  isNavIdDisabled,
  SIDEBAR_GROUPS,
} from '../Utils/sidebarCatalog';
import { getNavLabelForRole, getNavPathForRole } from '../Utils/sidebarLayoutUtils';
import { SidebarIcon } from '../Utils/sidebarIcons';
import { getSidebarActiveNavId } from '../Utils/sidebarActiveNav';

function collectGroupNavIds(node) {
  const ids = [];
  (node.children || []).forEach((c) => {
    if (c.kind === 'link') ids.push(c.navId);
  });
  return ids;
}

const SidebarNavRenderer = ({
  layout,
  roleId,
  location,
  navigate,
  onLogout,
}) => {
  const activeNavId = useMemo(
    () => getSidebarActiveNavId(location.pathname, location.search),
    [location.pathname, location.search]
  );

  const [openGroups, setOpenGroups] = useState({});

  useEffect(() => {
    if (!layout?.length) return;
    if (!activeNavId) {
      setOpenGroups({});
      return;
    }
    let matchedKey = null;
    layout.forEach((node, idx) => {
      if (node.kind !== 'group') return;
      const childIds = collectGroupNavIds(node);
      if (childIds.includes(activeNavId)) {
        matchedKey = node.groupId || String(idx);
      }
    });
    if (matchedKey) {
      setOpenGroups({ [matchedKey]: true });
    } else {
      setOpenGroups({});
    }
  }, [layout, activeNavId]);

  const handleNav = (navId) => {
    const meta = getCatalogItem(navId);
    const route = getNavPathForRole(roleId, navId, meta);
    if (route === '__logout__') {
      onLogout?.();
      return;
    }
    navigate(route);
  };

  const toggleGroup = (groupKey) => {
    setOpenGroups((prev) => {
      if (prev[groupKey]) {
        return {};
      }
      return { [groupKey]: true };
    });
  };

  const renderLink = (navId, inDropdown = false) => {
    if (isNavIdDisabled(navId)) return null;
    const meta = getCatalogItem(navId);
    const label = getNavLabelForRole(roleId, navId, meta);
    const icon = meta?.icon || 'BiDesktop';
    const active = activeNavId === navId;
    const idAttr = inDropdown
      ? active
        ? 'active-li-inDropdown'
        : 'li-button-inDropdown'
      : active
        ? 'active-li'
        : 'li-button';

    return (
      <li id="li-style" key={`${navId}-${inDropdown}`}>
        <button type="button" id={idAttr} className="border-0 bg-transparent w-100 text-start" onClick={() => handleNav(navId)}>
          <p id="icon-name">
            <SidebarIcon name={icon} />
            <span className="org-nav-text">{label}</span>
          </p>
        </button>
      </li>
    );
  };

  const renderGroup = (node, index) => {
    const groupMeta = SIDEBAR_GROUPS[node.groupId] || { label: node.groupId, icon: 'FcSettings' };
    const groupKey = node.groupId || `group-${index}`;
    const isOpen = !!openGroups[groupKey];
    const childActive = (node.children || []).some(
      (c) => c.kind === 'link' && c.navId === activeNavId
    );

    return (
      <React.Fragment key={groupKey}>
        <li id="li-style">
          <button
            type="button"
            id={childActive ? 'active-li' : 'li-button'}
            className="border-0 bg-transparent w-100 text-start"
            onClick={() => toggleGroup(groupKey)}
          >
            <p id="icon-name">
              <SidebarIcon name={groupMeta.icon} />
              <span className="org-nav-text">{groupMeta.label}</span>
              <span className="sidebar-nav-chevron org-nav-chevron">
                {isOpen ? (
                  <SidebarIcon name="FcCollapse" wrapped={false} />
                ) : (
                  <SidebarIcon name="FcExpand" wrapped={false} />
                )}
              </span>
            </p>
          </button>
        </li>
        <Collapse isOpen={isOpen}>
          <Card id="sidebar-dropdown-card">
            {(node.children || []).map((child) =>
              child.kind === 'link' ? renderLink(child.navId, true) : null
            )}
          </Card>
        </Collapse>
      </React.Fragment>
    );
  };

  if (!layout?.length) return null;

  return (
    <ul style={{ paddingLeft: 0 }}>
      {layout.map((node, index) =>
        node.kind === 'group' ? renderGroup(node, index) : renderLink(node.navId, false)
      )}
    </ul>
  );
};

export default SidebarNavRenderer;
